import { AxiosResponse } from 'axios'
import api from 'src/services/api'

export interface MinhaPendenciaItem {
  pendenciaId: string
  nome: string
  tipoRecebedor: string
  documento: string
  qtdCamposFaltantes: number
  requerAcaoSyn: boolean
}

export interface MinhasPendenciasResponse {
  temPendencia: boolean
  dataLimite?: string | null
  bloqueioAtivo: boolean
  total: number
  itens: MinhaPendenciaItem[]
}

export interface ContaBancariaSomenteLeitura {
  cpfCnpj: string
  nomeBanco: string
  codigoBanco: string
  agencia: string
  conta: string
  tipoConta: string
}

export interface CampoRegularizacao {
  campo: string
  rotulo: string
  textoApoio: string
  tipo: 'url' | 'data' | 'moeda' | 'texto' | 'telefone' | 'cep' | 'confirmacao' | string
  mascara?: string | null
  obrigatorio: boolean
}

export interface FormularioRegularizacaoResponse {
  pendenciaId: string
  nome: string
  tipoPessoa?: string | null
  tipoRecebedor?: string | null
  subtipoParceiro?: string | null
  posicao: number
  totalPendentes: number
  dataLimite?: string | null
  contaBancaria: ContaBancariaSomenteLeitura
  campos: CampoRegularizacao[]
}

export interface RegularizarRecebedorRequest {
  pendenciaId: string
  valores: Record<string, string>
}

export interface RegularizarRecebedorResponse {
  situacao: string
  proximaPendenciaId?: string | null
  restantes: number
}

export interface PainelPendenciaBacenTotalizadores {
  pendentes: number
  regularizados: number
  recusados: number
  falhas: number
  foraDeEscopo: number
}

export interface PainelPendenciaBacenItem {
  pendenciaId: string
  nome: string
  documento: string
  tipoPessoa?: string | null
  tipoRecebedor: string
  subtipoParceiro?: string | null
  medicoVinculado?: string | null
  tenants?: string | null
  situacao: string
  requerAcaoSyn: boolean
  camposObrigatoriosFaltantes: string[]
  camposOpcionaisFaltantes: string[]
  primeiraDeteccao: string
  ultimaVerificacao: string
  dataRegularizacao?: string | null
  diasEmAberto: number
  qtdPagamentos2025: number
  qtdPagamentos2026: number
  recebendoRecentemente: boolean
}

export interface PainelPendenciaBacenResponse {
  totalizadores: PainelPendenciaBacenTotalizadores
  total: number
  pagina: number
  tamanhoPagina: number
  dataLimite?: string | null
  bloqueioAtivo?: boolean
  itens: PainelPendenciaBacenItem[]
}

export interface TenantPainelPendenciaBacen {
  id: string
  descricao: string
}

export interface FiltroPainelPendenciaBacen {
  situacao?: number[]
  tipoRecebedor?: string[]
  subtipoParceiro?: string[]
  tenantId?: string
  busca?: string
  tipoPessoa?: 'PF' | 'PJ'
  requerAcaoSyn?: boolean
  recebendoRecentemente?: boolean
  temPagamentos2025?: boolean
  temPagamentos2026?: boolean
  pagina?: number
  tamanhoPagina?: number
}

function montarParamsPainel(filtro: FiltroPainelPendenciaBacen = {}) {
  const params = new URLSearchParams()
  params.set('pagina', String(filtro.pagina || 1))
  params.set('tamanhoPagina', String(filtro.tamanhoPagina || 12))

  ;(filtro.situacao || []).forEach(valor => {
    params.append('situacao', String(valor))
  })
  ;(filtro.tipoRecebedor || []).forEach(valor => {
    params.append('tipoRecebedor', valor)
  })
  ;(filtro.subtipoParceiro || []).forEach(valor => {
    params.append('subtipoParceiro', valor)
  })
  if (filtro.tenantId) params.set('tenantId', filtro.tenantId)
  if (filtro.busca) params.set('busca', filtro.busca)
  if (filtro.tipoPessoa) params.set('tipoPessoa', filtro.tipoPessoa)
  if (typeof filtro.requerAcaoSyn === 'boolean') {
    params.set('requerAcaoSyn', String(filtro.requerAcaoSyn))
  }
  if (typeof filtro.recebendoRecentemente === 'boolean') {
    params.set('recebendoRecentemente', String(filtro.recebendoRecentemente))
  }
  if (typeof filtro.temPagamentos2025 === 'boolean') {
    params.set('temPagamentos2025', String(filtro.temPagamentos2025))
  }
  if (typeof filtro.temPagamentos2026 === 'boolean') {
    params.set('temPagamentos2026', String(filtro.temPagamentos2026))
  }

  return params
}

export const obterMinhasPendencias = (): Promise<
  AxiosResponse<MinhasPendenciasResponse>
> => {
  return api.get('/RecebedorPendenciaBacen/minhas-pendencias')
}

export const obterFormularioRegularizacao = (
  pendenciaId: string
): Promise<AxiosResponse<FormularioRegularizacaoResponse>> => {
  return api.get(`/RecebedorPendenciaBacen/${pendenciaId}/formulario`)
}

export interface ItemCadastroPagarme {
  rotulo: string
  valor: string
}

export interface SecaoCadastroPagarme {
  titulo: string
  itens: ItemCadastroPagarme[]
}

export interface CadastroPagarmeRecebedorResponse {
  pendenciaId: string
  pagarmeRecebedorId: string
  secoes: SecaoCadastroPagarme[]
}

export const obterCadastroPagarmeRecebedor = (
  pendenciaId: string
): Promise<AxiosResponse<CadastroPagarmeRecebedorResponse>> => {
  return api.get(`/RecebedorPendenciaBacen/${pendenciaId}/cadastro-pagarme`)
}

export const regularizarRecebedor = (
  pendenciaId: string,
  body: RegularizarRecebedorRequest
): Promise<AxiosResponse<RegularizarRecebedorResponse>> => {
  return api.post(`/RecebedorPendenciaBacen/${pendenciaId}/regularizar`, body)
}

export const obterTenantsPainelPendenciaBacen = (): Promise<
  AxiosResponse<TenantPainelPendenciaBacen[]>
> => {
  return api.get('/RecebedorPendenciaBacen/painel/tenants')
}

export const obterPainelPendenciaBacen = (
  filtro: FiltroPainelPendenciaBacen = {}
): Promise<AxiosResponse<PainelPendenciaBacenResponse>> => {
  return api.get('/RecebedorPendenciaBacen/painel', {
    params: montarParamsPainel(filtro)
  })
}

export const exportarPainelPendenciaBacen = (
  filtro: FiltroPainelPendenciaBacen = {}
): Promise<AxiosResponse<Blob>> => {
  return api.get('/RecebedorPendenciaBacen/painel/exportar', {
    params: montarParamsPainel(filtro),
    responseType: 'blob'
  })
}

export const reverificarPendenciaBacen = (
  pendenciaId: string
): Promise<AxiosResponse<PainelPendenciaBacenItem>> => {
  return api.post(`/RecebedorPendenciaBacen/${pendenciaId}/reverificar`)
}
